import { useState, useEffect } from 'react';
import ReactApexChart from 'react-apexcharts';
import { TALENT_ARR } from '../../data/define';
import IcRedTriange from '../../assets/image/ic_triangle_small_red.svg';
import IcBlueTriange from '../../assets/image/ic_triangle_small_blue.svg';
import ToolTip from '../common/ToolTip';
import EmptyCase from '../common/EmptyCase';
import { useTranslation } from '../../hook/useTranslation';

function ChangeSummary({
  ComparisonType,
  changeToggle,
  compareResult,
  selectedReportId,
}) {
  const { t } = useTranslation();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [emptyOpen, setEmptyOpen] = useState(false);
  const [seriesImproved, setSeriesImproved] = useState([
    {
      data: [0, 0],
    },
  ]);
  const [seriesDecreased, setSeriesDecreased] = useState([
    {
      data: [0, 0],
    },
  ]);
  const isTwoResultType = changeToggle === ComparisonType.Previous;

  // '이전비교'시 데이터 세팅
  useEffect(() => {
    if (!compareResult || !isTwoResultType) return;
    if (compareResult?.mostImproved) {
      const targetIntelli = compareResult?.mostImproved?.intelligence;
      const baseScore =
        compareResult?.baseResult?.find((i) => i.intelligence === targetIntelli)?.score || 0;
      const currentScore =
        compareResult?.targetResult?.find((i) => i.intelligence === targetIntelli)?.score || 0;
      setSeriesImproved([
        {
          data: [baseScore, currentScore],
        },
      ]);
    }
    if (compareResult?.mostDecreased) {
      const targetIntelli = compareResult?.mostDecreased?.intelligence;
      const baseScore =
        compareResult?.baseResult?.find((i) => i.intelligence === targetIntelli)?.score || 0;
      const currentScore =
        compareResult?.targetResult?.find((i) => i.intelligence === targetIntelli)?.score || 0;
      setSeriesDecreased([
        {
          data: [baseScore, currentScore],
        },
      ]);
    }
  }, [compareResult, isTwoResultType]);

  // '누적비교'시 데이터 세팅
  useEffect(() => {
    if (!compareResult || isTwoResultType) return;
    if (compareResult?.mostImproved) {
      const targetIntelli = compareResult?.mostImproved?.intelligence;
      const targetResults = compareResult?.kidAverage?.find(
        (r) => r.intelligence === targetIntelli
      )?.result;
      const averageScore =
        (targetResults?.reduce((acc, cur) => acc + cur.score, 0) / targetResults?.length)?.toFixed(
          0
        ) || 0;
      const currentScore = targetResults?.find((i) => i.reportId === selectedReportId)?.score || 0;
      setSeriesImproved([
        {
          data: [averageScore, currentScore],
        },
      ]);
    }
    if (compareResult?.mostDecreased) {
      const targetIntelli = compareResult?.mostDecreased?.intelligence;
      const targetResults = compareResult?.kidAverage?.find(
        (r) => r.intelligence === targetIntelli
      )?.result;
      const averageScore =
        (targetResults?.reduce((acc, cur) => acc + cur.score, 0) / targetResults?.length)?.toFixed(
          0
        ) || 0;
      const currentScore = targetResults?.find((i) => i.reportId === selectedReportId)?.score || 0;
      setSeriesDecreased([
        {
          data: [averageScore, currentScore],
        },
      ]);
    }
  }, [compareResult, isTwoResultType, selectedReportId]);

  const options = {
    chart: {
      type: 'bar',
      height: 72,
      toolbar: {
        show: false,
      },
    },
    legend: {
      show: false,
    },
    plotOptions: {
      bar: {
        horizontal: true,
        distributed: true,
        borderRadius: 8,
        borderRadiusApplication: 'around',
        barHeight: '32px',
        dataLabels: {
          position: 'top',
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val}`,
      textAnchor: 'end',
      style: {
        colors: ['#ffffff'],
        fontSize: '14px',
      },
      offsetX: -8,
    },
    colors: ['#A4A4A4', '#74DFE0'],
    xaxis: {
      categories: isTwoResultType ? [t('report.summary.previous_short'), t('report.summary.current_short')] : [t('report.summary.average_short'), t('report.summary.current_short')],
      labels: {
        show: false,
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      labels: {
        show: true,
        style: {
          colors: ['#767676', '#5EBEBB'],
          fontSize: '14px',
          fontWeight: '700',
        },
        offsetX: 0,
        offsetY: 0,
      },
    },
    grid: {
      show: false,
    },
    stroke: {
      show: false,
    },
    tooltip: {
      enabled: false,
    },
  };

  useEffect(() => {
    setEmptyOpen(false);
  }, []);

  return (
    <div className="w-full relative pt-[20px] pb-[20px]">
      <div className="flex items-center justify-start gap-2 mb-[20px]">
        <div className="text-[#1C1C1C] font-[700] text-[20px] text-left]">{t('report.summary.title')}</div>
        <ToolTip
          open={tooltipOpen}
          setOpen={setTooltipOpen}
          text={
            changeToggle === ComparisonType.Previous
              ? t('report.summary.comparison_previous_tooltip')
              : t('report.summary.comparison_cumulative_tooltip')
          }
        />
      </div>
      <div className="w-full flex flex-col items-center justify-between">
        <div
          className="w-full min-h-[175px] rounded-[10px] p-[20px] bg-white flex flex-col items-start justify-center"
          style={{ boxShadow: '0px 0px 10px 0px rgba(29, 32, 32, 0.07)' }}
        >
          <div className="w-full flex items-center justify-between">
            <div className="font-[400] text-[14px] text-[#767676]">{t('report.summary.most_improved')}</div>
            <div className="flex items-center justify-center gap-1">
              <span className="text-[#969696] font-[400] text-[14px]">
                {t('report.summary.score', { score: compareResult?.mostImproved?.delta || 0 })}
              </span>
              <img src={IcRedTriange} alt="Red Triangle" />
            </div>
          </div>
          <div className="font-[600] text-[16px] text-[#363636] mb-[-20px]">
            {t('history.video.intelligence_title', { label: t(`talent.${TALENT_ARR.find((intel) => intel.id === compareResult?.mostImproved?.intelligence)?.key}`) })}
          </div>
          <div className="relative">
            <ReactApexChart
              options={options as any}
              series={seriesImproved}
              type="bar"
              height={140}
            />
          </div>
        </div>
        <div
          className="w-full min-h-[175px] rounded-[10px] p-[20px] bg-white flex flex-col items-start justify-center"
          style={{ boxShadow: '0px 0px 10px 0px rgba(29, 32, 32, 0.07)' }}
        >
          <div className="w-full flex items-center justify-between">
            <div className="font-[400] text-[14px] text-[#767676]">{t('report.summary.most_decreased')}</div>
            <div className="flex items-center justify-center gap-1">
              <span className="text-[#969696] font-[400] text-[14px]">
                {t('report.summary.score', { score: Math.abs(compareResult?.mostDecreased?.delta || 0) })}
              </span>
              <img src={IcBlueTriange} alt="Red Triangle" />
            </div>
          </div>
          <div className="font-[600] text-[16px] text-[#363636] mb-[-20px]">
            {t('history.video.intelligence_title', { label: t(`talent.${TALENT_ARR.find((intel) => intel.id === compareResult?.mostDecreased?.intelligence)?.key}`) })}
          </div>
          <div className="relative">
            <ReactApexChart
              options={options as any}
              series={seriesDecreased}
              type="bar"
              height={140}
            />
          </div>
        </div>
      </div>
      {/* <div className="bg-[#F8F8F8] p-[20px] rounded-xl text-[#363636] text-[14px] font-[400] mt-[20px]">
        {t('history.video.intelligence_title', { label: t(`talent.${TALENT_ARR.find((intel) => intel.id === compareResult?.mostImproved?.intelligence)?.key}`) })}
        <span className="font-[600]">{t('report.summary.score', { score: compareResult?.mostImproved?.score })}</span> {t('report.summary.excellent')}
        <br />
        {t('history.video.intelligence_title', { label: t(`talent.${TALENT_ARR.find((intel) => intel.id === compareResult?.mostDecreased?.intelligence)?.key}`) })}
        <span className="font-[600]">{t('report.summary.score', { score: compareResult?.mostDecreased?.score })}</span> {t('report.summary.needs_improvement')}
      </div> */}
      <EmptyCase open={emptyOpen} text={t('report.summary.title')} />
    </div>
  );
}

export default ChangeSummary;
